import { useState } from 'react';
import { toast } from '@/components/ui/toaster.jsx';
import { approveAddressChange, denyAddressChange } from '@/services/deals.service.js';

// Part B (N873AJ spec §2): a client requested a named-insured address change
// that moves the account to a DIFFERENT state. It is HELD (not written to CRM)
// and the client's sign step is blocked until the broker acts here. Approve
// writes the parked address to the CRM Account and clears the hold. Deny
// discards it (a reason is required) and the prior address stands. No client
// email is sent either way; the client just sees the sign step unblock (or the
// old address restored).
export default function AddressChangePendingBanner({ dealId, pending, onResolved }) {
  const [busy, setBusy] = useState(false);
  const [denying, setDenying] = useState(false);
  const [reason, setReason] = useState('');
  if (!pending) return null;

  const oldState = pending.state_old || '?';
  const newState = pending.state || '?';
  const addr = [pending.street, pending.city, [newState, pending.zip].filter(Boolean).join(' ')]
    .filter(Boolean).join(', ');

  const approve = async () => {
    setBusy(true);
    try {
      await approveAddressChange(dealId);
      toast.success('Address change approved and written to the account');
      onResolved?.();
    } catch (e) {
      toast.error(e.message || 'Approve failed');
    } finally {
      setBusy(false);
    }
  };

  const deny = async () => {
    if (!reason.trim()) { toast.error('A reason is required to deny'); return; }
    setBusy(true);
    try {
      await denyAddressChange(dealId, reason.trim());
      toast.success('Address change denied; the prior address stands');
      onResolved?.();
    } catch (e) {
      toast.error(e.message || 'Deny failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-card border border-warning/50 bg-warning/10 px-4 py-3">
      <div className="flex flex-wrap items-start gap-x-4 gap-y-2">
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold text-text">Address change needs review</p>
          <p className="mt-0.5 text-xs text-text-muted">
            The client is moving to a different state ({oldState} to {newState}). The address on file stands
            until you approve or deny; signing is not paused. Requested address: {addr}
          </p>
        </div>
        {!denying ? (
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={approve}
              className="inline-flex min-h-[32px] items-center rounded-[5px] border border-accent bg-accent px-3 py-1 text-[12.5px] font-medium text-white hover:brightness-110 disabled:opacity-50"
            >
              Approve
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => setDenying(true)}
              className="inline-flex min-h-[32px] items-center rounded-[5px] border border-border px-3 py-1 text-[12.5px] text-text-muted hover:border-border-strong hover:text-text disabled:opacity-50"
            >
              Deny
            </button>
          </div>
        ) : (
          <div className="flex w-full shrink-0 flex-col gap-2 sm:w-72">
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason for denial (required)"
              className="rounded-[5px] border border-border bg-surface px-2.5 py-1 text-[12.5px] text-text placeholder:text-text-dim"
            />
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={busy}
                onClick={deny}
                className="inline-flex min-h-[32px] items-center rounded-[5px] border border-accent px-3 py-1 text-[12.5px] font-medium text-accent hover:bg-accent hover:text-white disabled:opacity-50"
              >
                Confirm deny
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => { setDenying(false); setReason(''); }}
                className="inline-flex min-h-[32px] items-center rounded-[5px] border border-border px-3 py-1 text-[12.5px] text-text-muted hover:text-text disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
